import React, { useState, useEffect, useRef } from 'react';
import { Box, Tabs, Tab, Button } from '@mui/material';
import * as monaco from 'monaco-editor';
import { ProgressSpinner } from 'primereact/progressspinner';
import MonacoEditorWithFullscreen from './MonacoEditorWithFullscreen';
import { Save } from '@mui/icons-material';

// Initialize Monaco editor configuration for YAML
monaco.languages.register({ id: 'yaml' });
monaco.languages.setMonarchTokensProvider('yaml', {
  defaultToken: '',
  tokenPostfix: '.yaml',
  escapes: /\\(?:[abfnrtv\\"']|x[0-9A-Fa-f]{1,4}|u[0-9A-Fa-f]{4}|U[0-9A-Fa-f]{8})/,
  tokenizer: {
    root: [
      [/---/, 'delimiter.yaml'],
      [/\.\.\./, 'delimiter.yaml'],
      [/\s*[a-zA-Z][a-zA-Z0-9_-]*\s*:/, 'key'],
      [/\s*[a-zA-Z][a-zA-Z0-9_-]*\s*:\s*/, 'key'],
      [/\s*-\s*/, 'delimiter.array'],
      [/\s*:\s*/, 'delimiter'],
      [/\s*#.*$/, 'comment'],
      [/\s*[&*][a-zA-Z0-9_-]+\s*/, 'tag'],
      [/\s*!<[a-zA-Z0-9_-]+>\s*/, 'tag'],
      [/\s*![a-zA-Z0-9_-]+\s*/, 'tag'],
      // URL pattern - matches https:// and the protocol part
      [/(https?:\/\/)/, 'url.protocol'],
      // URL pattern - matches the rest of the URL
      [/(?<=:\/\/)[^\s]+/, 'url.path'],
      [/\s*"[^"]*"\s*/, 'value'],
      [/\s*'[^']*'\s*/, 'value'],
      [/\s*\d+\s*/, 'value'],
      [/\s*true\s*|\s*false\s*/, 'value'],
      [/\s*null\s*/, 'value'],
      [/\s*[a-zA-Z0-9_-]+\s*/, 'value'],
      [/\s*\n\s*/, 'white'],
      [/\s+/, {
        cases: {
          '@eos': 'white',
          '@default': 'white'
        }
      }]
    ]
  }
});

// Define custom theme
monaco.editor.defineTheme('customTheme', {
  base: 'vs-dark',
  inherit: true,
  rules: [
    { token: '', foreground: 'D4D4D4', background: '1E1E1E' },
    { token: 'string', foreground: '98c379' },
    { token: 'number', foreground: 'B5CEA8' },
    { token: 'keyword', foreground: '18dab8' },
    { token: 'comment', foreground: '6A9955' },
    { token: 'type', foreground: 'ff966b' },
    { token: 'function', foreground: 'DCDCAA' },
    { token: 'variable', foreground: '94151B' },
    { token: 'operator', foreground: 'D4D4D4' },
    { token: 'delimiter', foreground: 'D4D4D4' },
    { token: 'key', foreground: '18dab8' },
    { token: 'value', foreground: 'ff966b' },
    { token: 'url.protocol', foreground: 'ff966b' }, // Color for https://
    { token: 'url.path', foreground: 'ff966b' } // Color for the rest of the URL
  ],
  colors: {
    'editor.background': '#1e1e1e',
    'editor.foreground': '#D4D4D4',
    'editor.lineHighlightBackground': '#3D3D3D',
    'editor.selectionBackground': '#264F78',
    'editor.inactiveSelectionBackground': '#3A3D41',
    'editorCursor.foreground': '#AEAFAD',
    'editorWhitespace.foreground': '#404040',
    'editorLineNumber.foreground': '#858585',
    'editorLineNumber.activeForeground': '#C6C6C6',
    'editorGutter.background': '#1e1e1e',
    'editorGutter.modifiedBackground': '#0C7D9D',
    'editorGutter.addedBackground': '#587C0C',
    'editorGutter.deletedBackground': '#94151B',
    'editor.foldBackground': '#1e1e1e',
    'editorGutter.foldingControlForeground': '#FFFFFF',
    'editor.foldingRangeBackground': '#3D3D3D',
    'editor.foldingRangeBorder': '#4D4D4D',
    'editorGutter.border': '#4D4D4D',
    'editorGutter.foldingControlBackground': '#1e1e1e',
    'editorGutter.foldingControlHoverBackground': '#3D3D3D',
    'editorGutter.foldingControlHoverForeground': '#FFFFFF'
  }
});

// Set the theme
monaco.editor.setTheme('customTheme');

const CodeViewer = ({ files = [], onSave }) => {
  const [selectedTab, setSelectedTab] = useState(0);
  const [isMonacoLoaded, setIsMonacoLoaded] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editedFiles, setEditedFiles] = useState([]);
  const editorRef = useRef(null);
  const monacoEditorRef = useRef(null);

  const defaultOptions = {
    "acceptSuggestionOnCommitCharacter": true,
    "acceptSuggestionOnEnter": "on",
    "accessibilitySupport": "auto",
    "accessibilityPageSize": 10,
    "ariaLabel": "Editor content",
    "ariaRequired": false,
    "screenReaderAnnounceInlineSuggestion": true,
    "autoClosingBrackets": "languageDefined",
    "autoClosingComments": "languageDefined",
    "autoClosingDelete": "auto",
    "autoClosingOvertype": "auto",
    "autoClosingQuotes": "languageDefined",
    "autoIndent": "full",
    "autoSurround": "languageDefined",
    "bracketPairColorization": {
      "enabled": true,
      "independentColorPoolPerBracketType": false
    },
    "bracketPairGuides": {
      "bracketPairs": false,
      "bracketPairsHorizontal": false,
      "highlightActiveBracketPair": false,
      "indentation": true,
      "highlightActiveIndentation": true
    },
    "stickyTabStops": true,
    "tabSize": 2,
    "indentSize": 2,
    "insertSpaces": true,
    "detectIndentation": true,
    "colorDecorators": true,
    "colorDecoratorActivatedOn": "clickAndHover",
    "colorDecoratorsLimit": 500,
    "columnSelection": false,
    "comments": {
      "insertSpace": true,
      "ignoreEmptyLines": true
    },
    "contextmenu": true,
    "copyWithSyntaxHighlighting": true,
    "cursorBlinking": "blink",
    "cursorSmoothCaretAnimation": "off",
    "cursorStyle": "line",
    "cursorSurroundingLines": 0,
    "cursorSurroundingLinesStyle": "default",
    "cursorWidth": 0,
    "disableLayerHinting": false,
    "disableMonospaceOptimizations": false,
    "domReadOnly": false,
    "dragAndDrop": true,
    "emptySelectionClipboard": true,
    "dropIntoEditor": {
      "enabled": true,
      "showDropSelector": "afterDrop"
    },
    "stickyScroll": {
      "enabled": false,
      "maxLineCount": 5,
      "defaultModel": "outlineModel",
      "scrollWithEditor": true
    },
    "experimentalWhitespaceRendering": "svg",
    "extraEditorClassName": "",
    "fastScrollSensitivity": 5,
    "find": {
      "cursorMoveOnType": true,
      "seedSearchStringFromSelection": "always",
      "autoFindInSelection": "never",
      "globalFindClipboard": false,
      "addExtraSpaceOnTop": true,
      "loop": true
    },
    "fixedOverflowWidgets": false,
    "folding": true,
    "foldingStrategy": "auto",
    "foldingHighlight": false,
    "foldingImportsByDefault": false,
    "foldingMaximumRegions": 5000,
    "unfoldOnClickAfterEndOfLine": false,
    "codeLens": false,
    "fontFamily": "Consolas, 'Courier New', monospace",
    "fontLigatures2": false,
    "fontSize": 14,
    "fontWeight": "normal",
    "fontVariations": false,
    "formatOnPaste": false,
    "formatOnType": false,
    "glyphMargin": true,
    "gotoLocation": {
      "multiple": null,
      "multipleDefinitions": "peek",
      "multipleTypeDefinitions": "peek",
      "multipleDeclarations": "peek",
      "multipleImplementations": "peek",
      "multipleReferences": "peek",
      "alternativeDefinitionCommand": "editor.action.goToReferences",
      "alternativeTypeDefinitionCommand": "editor.action.goToReferences",
      "alternativeDeclarationCommand": "editor.action.goToReferences",
      "alternativeImplementationCommand": "",
      "alternativeReferenceCommand": ""
    },
    "hideCursorInOverviewRuler": false,
    "hover": {
      "enabled": true,
      "delay": 300,
      "sticky": true,
      "hidingDelay": 300,
      "above": true
    },
    "inDiffEditor": false,
    "letterSpacing": 0,
    "lightbulb": {
      "enabled": "onCode"
    },
    "lineDecorationsWidth": 10,
    "lineHeight": 24,
    "lineNumbers": "on",
    "lineNumbersMinChars": 5,
    "linkedEditing": false,
    "links": true,
    "matchBrackets": "always",
    "minimap": {
      "enabled": true,
      "autohide": false,
      "size": "proportional",
      "side": "right",
      "showSlider": "mouseover",
      "scale": 1,
      "renderCharacters": true,
      "maxColumn": 120,
      "showRegionSectionHeaders": true,
      "showMarkSectionHeaders": true,
      "sectionHeaderFontSize": 16,
      "sectionHeaderLetterSpacing": 1
    },
    "mouseStyle": "text",
    "mouseWheelScrollSensitivity": 1,
    "mouseWheelZoom": false,
    "multiCursorMergeOverlapping": true,
    "multiCursorModifier": "alt",
    "multiCursorPaste": "spread",
    "multiCursorLimit": 10000,
    "occurrencesHighlight": "singleFile",
    "overviewRulerBorder": true,
    "overviewRulerLanes": 2,
    "padding": {
      "top": 0,
      "bottom": 0,
      "left": 0,
      "right": 0
    },
    "pasteAs": {
      "enabled": true,
      "showPasteSelector": "afterPaste"
    },
    "parameterHints": {
      "enabled": true,
      "cycle": true
    },
    "peekWidgetDefaultFocus": "tree",
    "definitionLinkOpensInPeek": false,
    "quickSuggestions": {
      "other": "on",
      "comments": "off",
      "strings": "off"
    },
    "quickSuggestionsDelay": 10,
    "readOnly": true,
    "renameOnType": false,
    "renderControlCharacters": true,
    "renderFinalNewline": "on",
    "renderLineHighlight": "line",
    "renderLineHighlightOnlyWhenFocus": false,
    "renderValidationDecorations": "editable",
    "renderWhitespace": "selection",
    "revealHorizontalRightPadding": 0,
    "roundedSelection": true,
    "rulers": [],
    "scrollbar": {
      "vertical": "auto",
      "horizontal": "auto",
      "verticalScrollbarSize": 14,
      "horizontalScrollbarSize": 12,
      "scrollByPage": false,
      "ignoreHorizontalScrollbarInContentHeight": false
    },
    "scrollBeyondLastColumn": 4,
    "scrollBeyondLastLine": true,
    "scrollPredominantAxis": true,
    "selectionClipboard": true,
    "selectionHighlight": true,
    "selectOnLineNumbers": true,
    "showFoldingControls": "always",
    "showUnused": true,
    "showDeprecated": true,
    "inlayHints": {
      "enabled": "on",
      "fontSize": 0,
      "fontFamily": "",
      "padding": false
    },
    "snippetSuggestions": "inline",
    "smartSelect": {
      "selectLeadingAndTrailingWhitespace": true,
      "selectSubwords": true
    },
    "smoothScrolling": false,
    "stopRenderingLineAfter": 10000,
    "suggest": {
      "insertMode": "insert",
      "filterGraceful": true,
      "localityBonus": false,
      "shareSuggestSelections": false,
      "selectionMode": "always",
      "snippetsPreventQuickSuggestions": false,
      "showIcons": true,
      "showStatusBar": false,
      "preview": false,
      "showInlineDetails": true,
      "maxVisibleSuggestions": 0,
      "filteredTypes": {},
      "showMethods": true,
      "showFunctions": true,
      "showConstructors": true,
      "showDeprecated": true,
      "matchOnWordStartOnly": true,
      "showFields": true,
      "showVariables": true,
      "showClasses": true,
      "showStructs": true,
      "showInterfaces": true,
      "showModules": true,
      "showProperties": true,
      "showEvents": true,
      "showOperators": true,
      "showUnits": true,
      "showValues": true,
      "showConstants": true,
      "showEnums": true,
      "showEnumMembers": true,
      "showKeywords": true,
      "showWords": true,
      "showColors": true,
      "showFiles": true,
      "showReferences": true,
      "showCustomcolors": true,
      "showFolders": true,
      "showTypeParameters": true,
      "showSnippets": true,
      "showUsers": true,
      "showIssues": true
    },
    "inlineSuggest": {
      "enabled": true,
      "showToolbar": "onHover",
      "suppressSuggestions": false,
      "fontFamily": "default"
    },
    "inlineEdit": {
      "enabled": false,
      "showToolbar": "onHover",
      "fontFamily": "default"
    },
    "inlineCompletionsAccessibilityVerbose": true,
    "suggestFontSize": 0,
    "suggestLineHeight": 0,
    "suggestOnTriggerCharacters": true,
    "suggestSelection": "first",
    "tabCompletion": "on",
    "tabIndex": 0,
    "unicodeHighlight": {
      "nonBasicASCII": "inUntrustedWorkspace",
      "invisibleCharacters": true,
      "ambiguousCharacters": true,
      "includeComments": "inUntrustedWorkspace",
      "includeStrings": true,
      "allowedCharacters": {},
      "allowedLocales": {
        "_os": true,
        "_vscode": true
      }
    },
    "unusualLineTerminators": "prompt",
    "useShadowDOM": true,
    "useTabStops": true,
    "wordBreak": "normal",
    "wordSegmenterLocales": [],
    "wordSeparators": "`~!@#$%^&*()-=+[{]}\\|;:'\",.<>/?",
    "wordWrap": "off",
    "wordWrapBreakAfterCharacters": " \t})]?|/&.,;",
    "wordWrapBreakBeforeCharacters": "([{",
    "wordWrapColumn": 80,
    "wordWrapOverride1": "inherit",
    "wordWrapOverride2": "inherit",
    "defaultColorDecorators": false,
    "tabFocusMode": false,
    "wrappingIndent": {
      "wrappingIndent": "same"
    },
    "wrappingStrategy": {
      "wrappingStrategy": "simple"
    }
  }

  useEffect(() => {
    setEditedFiles(files);
  }, [files]);

  const handleEditorDidMount = (editor, monaco) => {
    monacoEditorRef.current = editor;
    editor.onDidChangeModelContent(() => {
      if (!isEditing) {  // Only set editing state if not already editing
        setIsEditing(true);
      }
      const currentFile = editedFiles[selectedTab];
      const newContent = editor.getValue();
      const updatedFiles = [...editedFiles];
      updatedFiles[selectedTab] = {
        ...currentFile,
        file_content: newContent
      };
      setEditedFiles(updatedFiles);
    });
  };

  const handleSave = async () => {
    const currentFile = editedFiles[selectedTab];
    onSave({
      file_name: currentFile.file_name,
      file_content: currentFile.file_content
    });
  };

  useEffect(() => {
    setIsMonacoLoaded(true);
    monaco.editor.remeasureFonts()
  }, [])

  useEffect(() => {
    if (monacoEditorRef.current) {
      monacoEditorRef.current.layout();
    }
  }, [isFullscreen]);

  const toggleFullscreen = () => {
    if (!isFullscreen) {
      const element = editorRef.current;
      if (element.requestFullscreen) {
        element.requestFullscreen();
      } else if (element.webkitRequestFullscreen) {
        element.webkitRequestFullscreen();
      } else if (element.msRequestFullscreen) {
        element.msRequestFullscreen();
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
      } else if (document.msExitFullscreen) {
        document.msExitFullscreen();
      }
    }
    setIsFullscreen(!isFullscreen);
  };

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('msfullscreenchange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('msfullscreenchange', handleFullscreenChange);
    };
  }, []);

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
  };

  const getLanguageFromFileName = (fileName) => {
    const extension = fileName.split('.').pop().toLowerCase();
    switch (extension) {
      case 'yml':
      case 'yaml':
        return 'yaml';
      case 'py':
        return 'python';
      case 'js':
      case 'jsx':
        return 'javascript';
      case 'ts':
      case 'tsx':
        return 'typescript';
      case 'json':
        return 'json';
      case 'md':
        return 'markdown';
      case 'html':
        return 'html';
      case 'css':
        return 'css';
      case 'sh':
        return 'shell';
      case 'xml':
        return 'xml';
      default:
        return 'yaml'; // Default to YAML for unknown extensions
    }
  };

  if (!Array.isArray(files) || files.length === 0) {
    return (
      <Box sx={{
        width: '100%',
        border: '1px solid var(--borderColor)',
        borderRadius: '4px',
        p: 2,
        textAlign: 'center',
        color: "var(--text-secondary)"
      }}>
        No files to display
      </Box>
    );
  }

  return (
    <Box
      ref={editorRef}
      sx={{
        width: '100%',
        height: isFullscreen ? '100vh' : 'auto',
        border: '1px solid var(--borderColor)',
        borderRadius: isFullscreen ? '0' : '8px',
        backgroundColor: '#1e1e1e',
        boxShadow: isFullscreen ? 'none' : '0 2px 8px rgba(0, 0, 0, 0.1)',
        overflow: 'hidden',
        position: isFullscreen ? 'fixed' : 'relative',
        top: isFullscreen ? 0 : 'auto',
        left: isFullscreen ? 0 : 'auto',
        zIndex: isFullscreen ? 9999 : 'auto'
      }}
    >
      <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        backgroundColor: '#1e1e1e',
        borderBottom: '1px solid #333',
        padding: '0 16px'
      }}>
        <Tabs
          value={selectedTab}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            minHeight: '40px',
            '& .MuiTab-root': {
              textTransform: 'none',
              minWidth: 'auto',
              padding: '8px 16px',
              fontSize: '14px',
              color: "var(--text-secondary)",
              '&.Mui-selected': {
                color: 'var(--text-primary)',
              },
            },
            '& .MuiTabs-indicator': {
              backgroundColor: 'var(--primary-color)',
            },
          }}
        >
          {editedFiles.map((file, index) => (
            <Tab
              key={index}
              label={file.file_name}
            />
          ))}
        </Tabs>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSave}
          startIcon={<Save />}
          sx={{
            textTransform: 'none',
            fontSize: '12px',
            height: '36px',
            backgroundColor: 'var(--primary-color)',
            '&:hover': {
              backgroundColor: 'var(--primary-color-light)'
            }
          }}
          disabled={!isEditing}
        >
          Save Changes
        </Button>
      </Box>
      {editedFiles.map((file, index) => (
        <Box
          key={index}
          role="tabpanel"
          hidden={selectedTab !== index}
          className="codeViewerUniqueClass"
          sx={{
            height: isFullscreen ? '100vh' : '90vh',
            overflow: 'hidden',
            position: 'relative',
            padding: 0,
            margin: 0,
            '& .monaco-editor': {
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              padding: 0,
              margin: 0,
              '& .monaco-editor-background': {
                padding: 0,
                margin: 0
              },
              '& .margin': {
                padding: 0,
                margin: 0
              },
              '& .monaco-scrollable-element': {
                padding: 0,
                margin: 0
              },
              '& .monaco-editor .overflow-guard': {
                padding: 0,
                margin: 0
              },
              '& .monaco-editor .view-lines': {
                padding: 0,
                margin: 0
              },
              '& .monaco-editor .view-overlays': {
                padding: 0,
                margin: 0
              }
            }
          }}
        >
          {selectedTab === index && !isMonacoLoaded ? (
            <Box sx={{
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: '#1e1e1e'
            }}>
              <ProgressSpinner
                style={{ width: '40px', height: '40px' }}
                strokeWidth="4"
                animationDuration=".5s"
              />
            </Box>
          ) : (
            <MonacoEditorWithFullscreen
              language={getLanguageFromFileName(file.file_name)}
              theme="customTheme"
              value={file.file_content}
              options={{
                ...defaultOptions,
                readOnly: false
              }}
              editorDidMount={handleEditorDidMount}
              height="90vh"
            />
          )}
        </Box>
      ))}
    </Box>
  );
};

export default CodeViewer; 